'use client';
import Link from "next/link";
import Image from "next/legacy/image";
import { IFooter } from "@/services/types";
import { Marquee } from "../../atoms/marque";
import { Mail, Phone, MapPin } from "lucide-react";

interface FooterProps extends IFooter {}

const Footer = ({ logo, social_midia, contact, site_map, parceiros, site_map_title }: FooterProps) => {
    return (
        <footer className="relative w-full bg-linear-270 from-stone-300 via-stone-50 via 66% to-white border-t-2 border-[#CECCCB]">
            {/* Desktop Footer */}
            <div className="hidden md:block w-full">
                <div className="container mx-auto  px-4 md:px-8 lg:px-16 py-12">
                    {/* Main Content Grid */}
                    <div className="grid grid-cols-12 gap-8 mb-12">
                        {/* Logo */}
                        <div className="col-span-3 flex flex-col">
                            <Link href="/" className="mb-8">
                                {logo?.url && (
                                    <Image
                                        src={logo.url}
                                        width={215}
                                        height={173}
                                        alt={logo.alternativeText || "Logo"}
                                        className="object-contain w-auto h-40"
                                    />
                                )}
                            </Link>
                        </div>

                        {/* Mapa do Site */}
                        <div className="col-span-3">
                            <h3 className="text-orangefpef text-base font-semibold mb-6 uppercase">
                                {site_map_title || "Mapa do Site"}
                            </h3>
                            <ul className="space-y-3">
                                {site_map?.map((item, index) => (
                                    <li key={index}>
                                        {item.external_link ? (
                                            <a
                                                href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-neutral-600 text-sm hover:text-orangefpef transition-colors duration-300"
                                            >
                                                {item.label}
                                            </a>
                                        ) : (
                                            <Link 
                                                href={item.url}
                                                className="text-neutral-600 text-sm hover:text-orangefpef transition-colors duration-300"
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Informações de Contato */}
                        <div className="col-span-6">
                            <div className="flex items-center justify-between mb-6">
                               
                                
                                {/* Social Media Icons */}
                                <div className="flex gap-3">
                                    {social_midia?.map((item, index) => (
                                        item?.logo?.url && (
                                            <Link
                                                key={index}
                                                href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-10 h-10 rounded-full border border-neutral-300 flex items-center justify-center transition-all duration-300 hover:border-orangefpef hover:scale-110"
                                            >
                                                <Image 
                                                    src={item.logo.url} 
                                                    alt={item.name} 
                                                    width={20} 
                                                    height={20}
                                                    className="object-contain"
                                                />
                                            </Link>
                                        )
                                    ))}
                                </div>
                            </div>
                            <ul className="space-y-4">
                                {contact?.map((item, index) => {
                                    const isPhone = item.label.includes("+238") || item.label.includes("800");
                                    const isEmail = item.label.includes("@");
                                    const isAddress = !isPhone && !isEmail;
                                    
                                    return (
                                        <li key={index} className="flex items-start gap-3">
                                            {isPhone && <Phone className="w-4 h-4 text-neutral-500 mt-1 flex-shrink-0" />}
                                            {isEmail && <Mail className="w-4 h-4 text-neutral-500 mt-1 flex-shrink-0" />}
                                            {isAddress && <MapPin className="w-4 h-4 text-neutral-500 mt-1 flex-shrink-0" />}
                                            <span className="text-neutral-600 text-sm leading-relaxed">
                                                {item.label}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="w-full h-px bg-neutral-300 mb-8" />

                    {/* Parceiros Section */}
                    <div className="relative overflow-hidden">
                        <Marquee className="py-6" pauseOnHover={true} repeat={4}>
                            {parceiros?.map((item, index) => (
                                <Link
                                    key={index}
                                    href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mx-8 transition-all duration-300 hover:scale-105 opacity-70 hover:opacity-100"
                                >
                                    {item?.logo?.url && (
                                        <Image
                                            src={item.logo.url}
                                            width={120}
                                            height={40}
                                            alt={item.name}
                                            className="object-contain w-auto h-10 grayscale hover:grayscale-0 transition-all duration-300"
                                        />
                                    )}
                                </Link>
                            ))}
                        </Marquee>
                    </div>
                </div>
            </div>

            {/* Mobile Footer */}
            <div className="md:hidden w-full">
                <div className="container mx-auto px-4 py-8">
                    {/* Logo e Social */}
                    <div className="mb-8">
                        <Link href="/" className="block mb-6">
                            {logo?.url && (
                                <Image
                                    src={logo.url}
                                    width={160}
                                    height={70}
                                    alt={logo.alternativeText || "Logo"}
                                    className="object-contain w-auto h-16"
                                />
                            )}
                        </Link>

                        
                    </div>

                    {/* Container com ordem invertida no mobile */}
                    <div className="flex flex-col-reverse">
                        {/* Mapa do Site */}
                        <div className="mb-8">
                            <h3 className="text-orangefpef text-sm font-semibold mb-4 uppercase">
                                {site_map_title || "Mapa do Site"}
                            </h3>
                            <ul className="space-y-2">
                                {site_map?.map((item, index) => (
                                    <li key={index}>
                                        {item.external_link ? (
                                            <a
                                                href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-neutral-600 text-sm hover:text-orangefpef transition-colors"
                                            >
                                                {item.label}
                                            </a>
                                        ) : (
                                            <Link
                                                href={item.url}
                                                className="text-neutral-600 text-sm hover:text-orangefpef transition-colors"
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Contactos */}
                        <div className="mb-8">
                           {/* Social Media */}
                            <div className="flex gap-3 mb-6">
                                {social_midia?.map((item, index) => (
                                    item?.logo?.url && (
                                        <Link
                                            key={index}
                                            href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-9 h-9 rounded-full border border-neutral-300 flex items-center justify-center hover:border-orangefpef transition-colors"
                                        >
                                            <Image
                                                src={item.logo.url}
                                                alt={item.name}
                                                width={18}
                                                height={18}
                                                className="object-contain"
                                            />
                                        </Link>
                                    )
                                ))}
                            </div>
                            <ul className="space-y-3">
                                {contact?.map((item, index) => {
                                    const isPhone = item.label.includes("+238") || item.label.includes("800");
                                    const isEmail = item.label.includes("@");
                                    const isAddress = !isPhone && !isEmail;

                                    return (
                                        <li key={index} className="flex items-start gap-2">
                                            {isPhone && <Phone className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" />}
                                            {isEmail && <Mail className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" />}
                                            {isAddress && <MapPin className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" />}
                                            <span className="text-neutral-600 text-xs leading-relaxed">
                                                {item.label}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="w-full h-px bg-neutral-300 mb-6" />

                    {/* Parceiros */}
                    <div className="relative overflow-hidden">
                        <Marquee className="py-4" pauseOnHover={true} repeat={4}>
                            {parceiros?.map((item, index) => (
                                <Link
                                    key={index}
                                    href={item.url.startsWith("http") ? item.url : `https://${item.url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mx-6 opacity-70 hover:opacity-100 transition-opacity"
                                >
                                    {item?.logo?.url && (
                                        <Image
                                            src={item.logo.url}
                                            width={100}
                                            height={32}
                                            alt={item.name}
                                            className="object-contain w-auto h-8 grayscale hover:grayscale-0 transition-all"
                                        />
                                    )}
                                </Link>
                            ))}
                        </Marquee>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
